import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { applyDesktopTheme, DESKTOP_THEME_KEY, DesktopTheme, readThemePreference, resolveDesktopTheme } from '@/utils/desktopTheme';

const DesktopThemeContext = createContext<{ theme: DesktopTheme; toggleTheme: () => void }>({ theme: 'light', toggleTheme: () => undefined });

export const DesktopThemeProvider = ({ children, enabled }: { children: ReactNode; enabled: boolean }) => {
  const [preference, setPreference] = useState(readThemePreference);
  const [systemDark, setSystemDark] = useState(() => typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const theme = resolveDesktopTheme(preference, systemDark);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const updateSystem = () => setSystemDark(query.matches);
    const updatePreference = (event: StorageEvent) => {
      if (event.key === DESKTOP_THEME_KEY || event.key === null) {
        setPreference(readThemePreference());
      }
    };
    updateSystem();
    query.addEventListener('change', updateSystem);
    window.addEventListener('storage', updatePreference);
    return () => {
      query.removeEventListener('change', updateSystem);
      window.removeEventListener('storage', updatePreference);
    };
  }, [enabled]);

  useEffect(() => {
    if (enabled) {
      applyDesktopTheme(theme);
    } else {
      document.documentElement.removeAttribute('data-desktop-theme');
      document.documentElement.style.removeProperty('color-scheme');
      document.body.removeAttribute('arco-theme');
    }
  }, [enabled, theme]);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setPreference(next);
    applyDesktopTheme(next);
    try {
      localStorage.setItem(DESKTOP_THEME_KEY, next);
    } catch {
      // The switch still works when persistent storage is unavailable.
    }
  };

  return <DesktopThemeContext.Provider value={{ theme, toggleTheme }}>{children}</DesktopThemeContext.Provider>;
};

export const useDesktopTheme = () => useContext(DesktopThemeContext);
